/**
 * The lobby WebSocket (ARCHITECTURE §7): connect → hello → welcome, automatic reconnect with exponential backoff
 * and jitter, server clock estimation from ping/pong, a client-side token bucket that keeps us under the server's
 * text rate limit (ICE candidate bursts queue instead of being rejected), liveness detection and typed dispatch.
 *
 * TEXT frames are JSON validated with `decodeServerMessage`; BINARY frames are relay game packets handed to the
 * binary listeners untouched.
 */
import {
  decodeServerMessage,
  encodeMessage,
  type ClientMessageInput,
  type MessageOf,
  type ServerMessage,
  type ServerMessageType,
} from '@hf/shared';
import { backoffDelay, ClockSync, TokenBucket } from './clock';
import { WS_OPEN, type NetEnv, type TimerId, type WebSocketLike } from './env';

export type SocketState =
  /** Never connected or disconnected by the user. */
  | 'idle'
  /** First connection in progress (connect() pending). */
  | 'connecting'
  /** Welcomed: the lobby is usable. */
  | 'online'
  /** Lost the connection after a welcome; retrying with backoff. */
  | 'reconnecting'
  /** Gave up for good (version mismatch, session replaced, user must reload). */
  | 'fatal';

export type WelcomeMessage = MessageOf<ServerMessage, 'welcome'>;
export type HelloMessage = Extract<ClientMessageInput, { type: 'hello' }>;

export const SOCKET_TIMING = {
  /** Attempts of the FIRST connection before connect() rejects (later drops retry forever). */
  initialAttempts: 4,
  backoffBaseMs: 500,
  backoffMaxMs: 10_000,
  /** No welcome this long after the socket opened → retry. */
  helloTimeoutMs: 10_000,
  /** Connecting (TCP/TLS/upgrade) this long → retry. */
  openTimeoutMs: 8_000,
  /** Clock-sync pings: a quick burst after welcome, then a slow cadence. */
  pingBurst: 4,
  pingBurstIntervalMs: 150,
  pingIntervalMs: 2_000,
  /** A ping got no reply (nor anything else) for this long → the socket is dead, reconnect. */
  silenceTimeoutMs: 12_000,
  /** In a match (`setMatchCadence(true)`): ping this often, so a dead local link is seen within ~3 s. */
  matchPingIntervalMs: 1_000,
  /** A ping unanswered (nothing at all received) this long → our own link is suspect (`onLink(false)`). */
  linkSuspectMs: 1_500,
  /** Our own text budget (the server allows burst 30 / 10 per s; keep headroom). */
  textBurst: 24,
  textPerSecond: 8,
  /** Drop queued messages beyond this (something is badly wrong). */
  maxQueue: 200,
} as const;

/** Close codes from the server that must not trigger a reconnect. */
const FATAL_CLOSE: Readonly<Record<number, string>> = {
  4000: 'This version of Hand Egg is out of date. Reload the page to update.',
  4001: 'A new version of Hand Egg is available. Reload the page to update.',
  4002: 'You connected from another tab or device.',
};

export class SocketError extends Error {
  constructor(
    message: string,
    readonly fatal: boolean,
  ) {
    super(message);
  }
}

type Handler = (msg: ServerMessage) => void;

export interface LobbySocketOptions {
  /** Build the hello for each (re)connection (token/name may change between attempts). */
  hello(): HelloMessage;
  /** Every state change (ui.net mirroring). `message` explains errors/reconnects. */
  onState?(state: SocketState, message?: string): void;
  /**
   * Our own link to the server went quiet (a ping unanswered for `linkSuspectMs`, nothing received) → false; the
   * first message after that → true. Lets a match tell "my connection" from "the opponent's" (QA r4).
   */
  onLink?(ok: boolean): void;
}

export class LobbySocket {
  readonly clock = new ClockSync();
  private ws: WebSocketLike | null = null;
  private url = '';
  private _state: SocketState = 'idle';
  private attempt = 0;
  private welcomed = false;
  private everWelcomed = false;
  private connectWaiters: { resolve(): void; reject(e: Error): void }[] = [];
  private readonly handlers = new Map<string, Set<Handler>>();
  private readonly anyHandlers = new Set<Handler>();
  private readonly binaryHandlers = new Set<(data: Uint8Array) => void>();
  private readonly bucket: TokenBucket;
  private readonly queue: string[] = [];
  private flushTimer: TimerId | null = null;
  private retryTimer: TimerId | null = null;
  private helloTimer: TimerId | null = null;
  private pingTimer: TimerId | null = null;
  private pingId = 0;
  private pingsSent = 0;
  /**
   * When the oldest ping still waiting for ANY reply was sent (null = none outstanding). Silence is measured from
   * it, not from the last message: in a long-hidden tab Chrome runs chained timers about once a minute, so on a
   * quiet lobby "nothing received for 12 s" is normal there and must not drop a healthy socket.
   */
  private unansweredSince: number | null = null;
  private suspectTimer: TimerId | null = null;
  private linkOk = true;
  private matchCadence = false;
  private lastWelcome: WelcomeMessage | null = null;

  constructor(
    private readonly env: NetEnv,
    private readonly opts: LobbySocketOptions,
  ) {
    this.bucket = new TokenBucket(SOCKET_TIMING.textBurst, SOCKET_TIMING.textPerSecond, env.now());
  }

  get state(): SocketState {
    return this._state;
  }

  /** Welcomed and the socket is open. */
  get online(): boolean {
    return this._state === 'online' && this.ws?.readyState === WS_OPEN;
  }

  /** The raw socket is open (binary relay frames can flow even before a re-welcome). */
  get open(): boolean {
    return this.ws?.readyState === WS_OPEN && this.welcomed;
  }

  get welcome(): WelcomeMessage | null {
    return this.lastWelcome;
  }

  /** Connect (or join the pending connection). Resolves after `welcome`. */
  connect(url: string): Promise<void> {
    if (this._state === 'online' && url === this.url) return Promise.resolve();
    if (this._state === 'fatal') {
      return Promise.reject(new SocketError('cannot reconnect: reload the page', true));
    }
    const p = new Promise<void>((resolve, reject) => this.connectWaiters.push({ resolve, reject }));
    if ((this._state === 'connecting' || this._state === 'reconnecting') && url === this.url) return p;
    this.teardown();
    this.url = url;
    this.attempt = 0;
    this.everWelcomed = false;
    this.setState('connecting');
    this.open_();
    return p;
  }

  /** User disconnect: no reconnect. */
  close(): void {
    this.teardown();
    this.queue.length = 0;
    this.rejectWaiters(new SocketError('disconnected', false));
    if (this._state !== 'fatal') this.setState('idle');
  }

  /** Send a JSON message (queued under the rate limit). False when not connected (nothing is queued). */
  send(msg: ClientMessageInput): boolean {
    if (!this.open) return false;
    const raw = encodeMessage(msg);
    if (this.queue.length === 0 && this.bucket.take(this.env.now())) {
      this.rawSend(raw);
      return true;
    }
    if (this.queue.length >= SOCKET_TIMING.maxQueue) {
      this.env.log.warn('outgoing queue full, dropping', msg.type);
      return false;
    }
    this.queue.push(raw);
    this.scheduleFlush();
    return true;
  }

  sendBinary(data: Uint8Array): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== WS_OPEN || !this.welcomed) return false;
    try {
      ws.send(data);
      return true;
    } catch {
      return false;
    }
  }

  on<T extends ServerMessageType>(type: T, handler: (msg: MessageOf<ServerMessage, T>) => void): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    const h = handler as Handler;
    set.add(h);
    return () => void set.delete(h);
  }

  onAny(handler: Handler): () => void {
    this.anyHandlers.add(handler);
    return () => void this.anyHandlers.delete(handler);
  }

  onBinary(handler: (data: Uint8Array) => void): () => void {
    this.binaryHandlers.add(handler);
    return () => void this.binaryHandlers.delete(handler);
  }

  /** Our own link to the server answers (false while a ping has gone unanswered past `linkSuspectMs`). */
  get linkHealthy(): boolean {
    return this.linkOk;
  }

  /** In a match: ping every `matchPingIntervalMs` so a dead local link is noticed fast (off again after it). */
  setMatchCadence(on: boolean): void {
    if (this.matchCadence === on) return;
    this.matchCadence = on;
    if (on && this.open && this.pingTimer !== null && this.pingsSent >= SOCKET_TIMING.pingBurst)
      this.schedulePing(SOCKET_TIMING.matchPingIntervalMs);
  }

  // ── Connection lifecycle ──────────────────────────────────────────────────────────────────────────

  private setState(s: SocketState, message?: string): void {
    if (this._state === s && message === undefined) return;
    this._state = s;
    this.opts.onState?.(s, message);
  }

  private open_(): void {
    let ws: WebSocketLike;
    try {
      ws = this.env.createSocket(this.url);
    } catch (e) {
      this.env.log.warn('socket constructor failed', e);
      this.onDrop(1006, 'cannot open socket');
      return;
    }
    this.ws = ws;
    this.welcomed = false;
    try {
      ws.binaryType = 'arraybuffer';
    } catch {
      /* fakes */
    }
    this.armHelloTimer(SOCKET_TIMING.openTimeoutMs);
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.unansweredSince = null;
      this.armHelloTimer(SOCKET_TIMING.helloTimeoutMs);
      try {
        ws.send(encodeMessage(this.opts.hello()));
      } catch {
        /* closing */
      }
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      this.unansweredSince = null;
      this.setLink(true);
      this.onFrame(ev.data);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.onDrop(ev.code, ev.reason);
    };
    ws.onerror = () => {
      // onclose follows; nothing to do here.
    };
  }

  private armHelloTimer(ms: number): void {
    this.clearTimer('helloTimer');
    this.helloTimer = this.env.setTimeout(() => {
      this.helloTimer = null;
      if (this.welcomed) return;
      this.env.log.warn('no welcome in time; retrying');
      this.dropSocket();
      this.onDrop(4003, 'hello timeout');
    }, ms);
  }

  private onFrame(data: unknown): void {
    if (typeof data === 'string') {
      const r = decodeServerMessage(data);
      if (!r.ok) {
        this.env.log.warn('invalid server message', r.error);
        return;
      }
      this.dispatch(r.msg);
      return;
    }
    let bytes: Uint8Array | null = null;
    if (data instanceof ArrayBuffer) bytes = new Uint8Array(data);
    else if (ArrayBuffer.isView(data)) bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    if (!bytes) return;
    for (const h of this.binaryHandlers) h(bytes);
  }

  private dispatch(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        this.onWelcome(msg);
        break;
      case 'pong':
        this.clock.add(msg.clientTime, this.env.now(), msg.serverTime);
        break;
      case 'error':
        // A server that requires a signed-in account says so in the message (older builds just see "update").
        if (msg.code === 'update_required' && /sign in/i.test(msg.message)) this.fatal(msg.message);
        else if (msg.code === 'version_mismatch' || msg.code === 'update_required')
          this.fatal(FATAL_CLOSE[4001]!);
        else if (msg.code === 'protocol_mismatch') this.fatal(FATAL_CLOSE[4000]!);
        break;
      default:
        break;
    }
    const set = this.handlers.get(msg.type);
    if (set) for (const h of [...set]) safeCall(h, msg, this.env);
    for (const h of [...this.anyHandlers]) safeCall(h, msg, this.env);
  }

  private onWelcome(msg: WelcomeMessage): void {
    this.clearTimer('helloTimer');
    this.welcomed = true;
    this.everWelcomed = true;
    this.attempt = 0;
    this.lastWelcome = msg;
    this.clock.seed(msg.serverTime, this.env.now());
    this.setState('online');
    this.pingsSent = 0;
    this.schedulePing(0);
    const waiters = this.connectWaiters.splice(0);
    for (const w of waiters) w.resolve();
    if (this.queue.length > 0) this.scheduleFlush();
  }

  private fatal(message: string): void {
    this.teardown();
    this.queue.length = 0;
    this.setState('fatal', message);
    this.rejectWaiters(new SocketError(message, true));
  }

  private onDrop(code: number, reason: string): void {
    const fatalMsg = FATAL_CLOSE[code];
    if (fatalMsg && this._state === 'fatal') return; // the error frame already said why (keep its message)
    if (fatalMsg) {
      this.fatal(fatalMsg);
      return;
    }
    this.dropSocket();
    if (this._state === 'idle' || this._state === 'fatal') return;
    const attempt = this.attempt++;
    if (!this.everWelcomed && attempt + 1 >= SOCKET_TIMING.initialAttempts) {
      this.setState('idle', "Can't reach the game server. Check your connection and try again.");
      this.rejectWaiters(new SocketError(`cannot reach the server (${code} ${reason})`.trim(), false));
      return;
    }
    const delay = backoffDelay(
      attempt,
      this.env.random,
      SOCKET_TIMING.backoffBaseMs,
      SOCKET_TIMING.backoffMaxMs,
    );
    this.setState(
      this.everWelcomed ? 'reconnecting' : 'connecting',
      this.everWelcomed ? 'Reconnecting…' : undefined,
    );
    this.clearTimer('retryTimer');
    this.retryTimer = this.env.setTimeout(() => {
      this.retryTimer = null;
      if (this._state === 'reconnecting' || this._state === 'connecting') this.open_();
    }, delay);
  }

  /** Detach and close the current socket without changing the state machine. */
  private dropSocket(): void {
    const ws = this.ws;
    this.ws = null;
    this.welcomed = false;
    this.clearTimer('helloTimer');
    this.clearTimer('pingTimer');
    this.clearTimer('flushTimer');
    this.clearTimer('suspectTimer');
    this.unansweredSince = null;
    // A dropped socket is the state machine's business now ('reconnecting'): the link verdict starts over.
    this.linkOk = true;
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try {
        ws.close(1000, 'bye');
      } catch {
        /* already closed */
      }
    }
  }

  private teardown(): void {
    this.dropSocket();
    this.clearTimer('retryTimer');
  }

  private rejectWaiters(e: Error): void {
    const waiters = this.connectWaiters.splice(0);
    for (const w of waiters) w.reject(e);
  }

  private clearTimer(name: 'flushTimer' | 'retryTimer' | 'helloTimer' | 'pingTimer' | 'suspectTimer'): void {
    const t = this[name];
    if (t !== null) this.env.clearTimeout(t);
    this[name] = null;
  }

  // ── Outgoing queue / pings ────────────────────────────────────────────────────────────────────────

  private rawSend(raw: string): void {
    try {
      this.ws?.send(raw);
    } catch {
      /* closing: the reconnect path takes over */
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null || !this.open) return;
    const wait = Math.max(1, this.bucket.waitMs(this.env.now()));
    this.flushTimer = this.env.setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, wait);
  }

  private flush(): void {
    while (this.queue.length > 0 && this.open && this.bucket.take(this.env.now()))
      this.rawSend(this.queue.shift()!);
    if (this.queue.length > 0) this.scheduleFlush();
  }

  private schedulePing(delay: number): void {
    this.clearTimer('pingTimer');
    this.pingTimer = this.env.setTimeout(() => {
      this.pingTimer = null;
      this.ping();
    }, delay);
  }

  /** Check the link once the oldest unanswered ping is `linkSuspectMs` old (cleared by any message). */
  private armSuspect(delay: number = SOCKET_TIMING.linkSuspectMs): void {
    this.clearTimer('suspectTimer');
    this.suspectTimer = this.env.setTimeout(() => {
      this.suspectTimer = null;
      const since = this.unansweredSince;
      if (since === null || !this.open) return; // answered (any message clears it) or dropped
      const age = this.env.now() - since;
      if (age >= SOCKET_TIMING.linkSuspectMs) this.setLink(false);
      else this.armSuspect(SOCKET_TIMING.linkSuspectMs - age);
    }, delay);
  }

  private setLink(ok: boolean): void {
    if (this.linkOk === ok) return;
    this.linkOk = ok;
    this.opts.onLink?.(ok);
  }

  private ping(): void {
    if (!this.open) return;
    const now = this.env.now();
    if (this.unansweredSince !== null && now - this.unansweredSince > SOCKET_TIMING.silenceTimeoutMs) {
      this.env.log.warn('server silent; reconnecting');
      this.dropSocket();
      this.onDrop(4005, 'silent');
      return;
    }
    // Clock pings never compete with real traffic for the rate budget.
    if (this.queue.length === 0 && this.bucket.take(now)) {
      this.pingId = (this.pingId + 1) >>> 0;
      this.rawSend(encodeMessage({ type: 'ping', id: this.pingId, clientTime: now }));
      this.pingsSent++;
      if (this.unansweredSince === null) {
        this.unansweredSince = now;
        this.armSuspect();
      }
    }
    this.schedulePing(
      this.pingsSent < SOCKET_TIMING.pingBurst
        ? SOCKET_TIMING.pingBurstIntervalMs
        : this.matchCadence
          ? SOCKET_TIMING.matchPingIntervalMs
          : SOCKET_TIMING.pingIntervalMs,
    );
  }
}

const safeCall = (h: Handler, msg: ServerMessage, env: NetEnv): void => {
  try {
    h(msg);
  } catch (e) {
    env.log.warn(`handler for ${msg.type} threw`, e);
  }
};
